import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Booking } from '../../hooks/api/booking';
import { Lesson, useDeleteLessons } from '../../hooks/api/lesson';
import {
  convertDateToTimeString,
  formatDateToDayAndMonth,
  formatDateToWeekday,
} from '../../util/dates';
import DesktopParticipantsList from './DesktopParticipantsList';
import UpdateLessonModal from './UpdateLessonModal';

interface InstructorLessonDetailsDialogProps {
  bookings: Booking[];
  lesson?: Lesson;
  onClose: () => void;
  open: boolean;
}

const formatLessonType = (type: Lesson['type']) => {
  return type === 'nogi' ? 'No-Gi' : type.toUpperCase();
};

export default function InstructorLessonDetailsDialog({
  bookings,
  lesson,
  onClose,
  open,
}: InstructorLessonDetailsDialogProps) {
  const [isUpdateLessonModalOpen, setIsUpdateLessonModalOpen] = useState(false);
  const deleteLessonMutation = useDeleteLessons();

  const approvedBookings = useMemo(() => {
    return bookings.filter((booking) => booking.status === 'approved').length;
  }, [bookings]);
  const pendingBookings = useMemo(() => {
    return bookings.filter((booking) => booking.status === 'pending').length;
  }, [bookings]);
  const occupancy = lesson
    ? lesson.maxSeats === 0
      ? 0
      : Math.min((bookings.length / lesson.maxSeats) * 100, 100)
    : 0;

  if (!lesson) {
    return null;
  }

  const lessonDate = new Date(lesson.startDate);
  const startTime = convertDateToTimeString(new Date(lesson.startDate));
  const endTime = convertDateToTimeString(new Date(lesson.endDate));
  const isPastLesson = new Date() > new Date(lesson.startDate);

  const handleDeleteLesson = () => {
    const shouldDelete = window.confirm('¿Seguro que quieres eliminar esta clase?');
    if (!shouldDelete) {
      return;
    }

    deleteLessonMutation.mutate(
      { lessonId: lesson.id },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  return (
    <>
      <UpdateLessonModal
        isOpen={isUpdateLessonModalOpen}
        close={() => setIsUpdateLessonModalOpen(false)}
        initialValues={{
          startDate: lesson.startDate,
          endDate: lesson.endDate,
          type: lesson.type,
          accessLevel: lesson.accessLevel ?? 'normal',
          professorId: lesson.professor.id,
        }}
        lessonId={lesson.id}
      />

      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ px: 3, pt: 3, pb: 2, pr: 7 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip
                  label={formatLessonType(lesson.type)}
                  size="small"
                  sx={{ bgcolor: '#f3f3f3', color: '#111111', fontWeight: 700 }}
                />
                <Chip
                  label={isPastLesson ? 'Finalizada' : 'Programada'}
                  size="small"
                  sx={{
                    bgcolor: isPastLesson ? '#ececec' : '#eaeaea',
                    color: '#111111',
                    fontWeight: 700,
                  }}
                />
              </Stack>

              <IconButton
                aria-label="Cerrar detalle de clase"
                onClick={onClose}
                sx={{ position: 'absolute', right: 12, top: 12 }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#111111' }}>
                {`${formatDateToWeekday(lessonDate, { weekday: 'long' })} ${formatDateToDayAndMonth(
                  lessonDate,
                )}`}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                {`${startTime} - ${endTime}`}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 3 }}>
          <Stack spacing={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 4,
                border: '1px solid rgba(18,18,18,0.08)',
                backgroundColor: '#f7f7f7',
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar
                    src={lesson.professor.image}
                    alt="Profesor"
                    sx={{ width: 56, height: 56 }}
                  />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Instructor
                    </Typography>
                    <Typography sx={{ fontWeight: 700 }}>
                      {`${lesson.professor.firstName} ${lesson.professor.lastName}`}
                    </Typography>
                  </Box>
                </Stack>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  width={{ xs: '100%', md: 'auto' }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<EditOutlinedIcon />}
                    disabled={isPastLesson}
                    onClick={() => setIsUpdateLessonModalOpen(true)}
                    sx={{ borderRadius: 3, textTransform: 'none' }}
                  >
                    Editar clase
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    disabled={deleteLessonMutation.isLoading}
                    onClick={handleDeleteLesson}
                    sx={{ borderRadius: 3, textTransform: 'none', boxShadow: 'none' }}
                  >
                    Eliminar
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                gap: 1.5,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid rgba(18,18,18,0.08)',
                  backgroundColor: '#f7f7f7',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Ocupación
                </Typography>
                <Typography variant="h5" sx={{ mt: 0.25, fontWeight: 700 }}>
                  {`${bookings.length}/${lesson.maxSeats}`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Reservas registradas
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={occupancy}
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    bgcolor: 'rgba(17,17,17,0.08)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 999,
                      bgcolor: '#111111',
                    },
                  }}
                />
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid rgba(18,18,18,0.08)',
                  backgroundColor: '#f7f7f7',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Aprobadas
                </Typography>
                <Typography variant="h5" sx={{ mt: 0.25, fontWeight: 700 }}>
                  {approvedBookings}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Asistencias confirmadas
                </Typography>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid rgba(18,18,18,0.08)',
                  backgroundColor: '#f7f7f7',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Pendientes
                </Typography>
                <Typography variant="h5" sx={{ mt: 0.25, fontWeight: 700 }}>
                  {pendingBookings}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Solicitudes por revisar
                </Typography>
              </Paper>
            </Box>

            <DesktopParticipantsList bookings={bookings} lessonId={lesson.id} />
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
